import React from 'react';
import { Wallet, Plane, Building2, PiggyBank } from 'lucide-react';

interface BudgetSummaryProps {
  data: {
    totalRequired: number;
    currentSavings: number;
    monthlyExpenses: number;
    initialCosts: number;
    setupCosts: number;
    survivalMonths: number;
    pvtDuration: string;
  };
}

export function BudgetSummary({ data }: BudgetSummaryProps) {
  const { totalRequired, currentSavings, monthlyExpenses, initialCosts, setupCosts } = data;

  const formatNumber = (num: number) => num.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const cards = [
    {
      label: 'Dépenses mensuelles',
      value: monthlyExpenses,
      sub: 'Loyer, courses, transport...',
      icon: Wallet,
      color: 'emerald',
    },
    {
      label: 'Avant le départ',
      value: initialCosts,
      sub: "Billet d'avion, PVT, assurance",
      icon: Plane,
      color: 'accent',
    },
    {
      label: "Coûts d'installation",
      value: setupCosts,
      sub: 'Caution, mobilier, premiers achats',
      icon: Building2,
      color: 'amber',
    },
  ];

  const colorMap: Record<string, { bg: string; iconBg: string; iconText: string; border: string }> = {
    emerald: {
      bg: 'bg-emerald-50 dark:bg-emerald-900/10',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
      iconText: 'text-emerald-600 dark:text-emerald-400',
      border: 'border-emerald-100 dark:border-emerald-800/30',
    },
    accent: {
      bg: 'bg-accent-50 dark:bg-accent-900/10',
      iconBg: 'bg-accent-100 dark:bg-accent-900/30',
      iconText: 'text-accent-600 dark:text-accent-400',
      border: 'border-accent-100 dark:border-accent-800/30',
    },
    amber: {
      bg: 'bg-amber-50 dark:bg-amber-900/10',
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      iconText: 'text-amber-600 dark:text-amber-400',
      border: 'border-amber-100 dark:border-amber-800/30',
    },
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((card) => {
          const colors = colorMap[card.color];
          return (
            <div
              key={card.label}
              className={`${colors.bg} rounded-xl p-4 sm:p-5 border ${colors.border} transition-all duration-200 hover:shadow-md`}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className={`p-2 ${colors.iconBg} rounded-lg`}>
                  <card.icon className={`w-4 h-4 ${colors.iconText}`} />
                </div>
                <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {card.label}
                </span>
              </div>
              <div className="text-xl font-black text-gray-900 dark:text-white tabular-nums">
                {formatNumber(card.value)} EUR
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{card.sub}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
        <div className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <PiggyBank className="w-4 h-4 text-primary-500" />
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Résumé du budget total</span>
          </div>

          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Dépenses mensuelles x {data.pvtDuration} mois</span>
              <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{formatNumber(monthlyExpenses * Number(data.pvtDuration))} EUR</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Dépenses avant le départ</span>
              <span className="font-semibold text-gray-900 dark:text-white tabular-nums">+ {formatNumber(initialCosts)} EUR</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Coûts d'installation</span>
              <span className="font-semibold text-gray-900 dark:text-white tabular-nums">+ {formatNumber(setupCosts)} EUR</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-900 dark:text-white">Budget total requis</span>
              <span className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                {formatNumber(totalRequired)} EUR
              </span>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Vos économies</span>
              <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                {formatNumber(currentSavings)} EUR
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
